/*
 * Copyright (c) 2021. Dave van Rijn Development
 */
import './style/topButton.scss';

import React, {MouseEventHandler, PureComponent, ReactElement} from 'react';
import classNames from "classnames";
import {AwesomeIcon, darkenColor} from "../../../index";
import {ControlContext} from "../util/controlContext";
import {convertColor} from "../util/colorUtil";
import { IconName } from '@fortawesome/fontawesome-svg-core';

export interface TopButtonProps {
    onClick?: MouseEventHandler;
    onMouseEnter?: MouseEventHandler;
    onMouseLeave?: MouseEventHandler;
    className: string;
    iconClassName: string;
    icon: IconName | ReactElement;
    disabled?: boolean;
    color?: string;
    iconColor?: string;
}

interface State {
    visible: boolean;
    isHovered: boolean;
}

export default class TopButton extends PureComponent<TopButtonProps, State> {
    declare context: React.ContextType<typeof ControlContext>
    static contextType = ControlContext;
    static defaultProps = {
        disabled: false,
        className: '',
        iconClassName: '',
        icon: 'chevron-up',
    };

    private isScrolling: boolean = false;

    state: State = {
        visible: false,
        isHovered: false,
    }

    onMouseEnter = (evt: React.MouseEvent) => {
        const {onMouseEnter} = this.props;
        if (!this.state.isHovered) this.setState({isHovered: true});
        if (onMouseEnter) onMouseEnter(evt);
    };

    onMouseLeave = (evt: React.MouseEvent) => {
        const {onMouseLeave} = this.props;
        if (this.state.isHovered) this.setState({isHovered: false});
        if (onMouseLeave) onMouseLeave(evt);
    };

    onClick = (evt: React.MouseEvent) => {
        const {onClick, disabled} = this.props;
        if (!disabled) {
            if (onClick) onClick(evt);
            else
                window.scrollTo({top: 0, behavior: "smooth"});
        }
    };

    getBaseColor = (): string => convertColor(this.context.baseColor);
    getContrastColor = (): string => convertColor(this.context.contrastColor);

    getBackgroundColor = (): string => {
        const {color} = this.props, {isHovered} = this.state, baseColor = this.getBaseColor();
        let bgColor: string = convertColor(color === undefined ? baseColor : color);
        if (isHovered) bgColor = darkenColor(bgColor, 20);
        return bgColor;
    };

    getIconColor = (): string => {
        const {iconColor} = this.props, contrastColor = this.getContrastColor();
        return convertColor(iconColor === undefined ? contrastColor : iconColor);
    }

    scrollListener = () => {
        const {visible} = this.state, {disabled} = this.props;
        if (!this.isScrolling) {
            this.isScrolling = true;
            window.requestAnimationFrame(() => {
                const scrollPosition: number = window.scrollY;
                if (scrollPosition > 0) {
                    if (!visible)
                        this.setState({visible: !disabled});
                } else if (visible)
                    this.setState({visible: false});
                this.isScrolling = false;
            });
        }
    };

    testInitialScrollPos = () => {
        const {disabled} = this.props, scrollPos = window.scrollY;
        this.setState({visible: !disabled && scrollPos > 0});
    }

    renderIcon = () => {
        const {icon, iconClassName} = this.props;
        let elem: ReactElement;
        if (typeof icon === 'string')
            elem = <AwesomeIcon name={icon} className={classNames('dvr-top-icon', iconClassName)}
                                style={{color: this.getIconColor()}}/>;
        else elem = icon;
        return elem;
    };

    componentDidMount = () => {
        window.addEventListener('scroll', this.scrollListener, {passive: true});
        this.testInitialScrollPos();
    };

    componentWillUnmount = () => {
        window.removeEventListener('scroll', this.scrollListener);
    };

    render = () => {
        const {visible} = this.state, {disabled, className} = this.props;
        return (
            <div className={classNames('dvr-top-btn', className, (!visible || disabled) && 'hidden')}
                 onClick={this.onClick} onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}
                 style={{backgroundColor: this.getBackgroundColor()}}>
                {this.renderIcon()}
                <label className='hover-label'
                       style={{backgroundColor: this.getBackgroundColor(), color: this.getIconColor()}}>
                    Naar boven</label>
            </div>
        )
    };
}